import React from 'react';
import { CartItem, useCartStore } from '@/ui/store/cart';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { twJoin } from 'tailwind-merge';
import CartProductImage from '../../CartProductImage';
import { SubscriptionInfo } from '../../SubscriptionInfo';
import { CartItemVariant } from '../../types/CartItemVariant';

export interface BundleProductCartItemProps {
  product: CartItem;
  disableUpdate?: boolean;
  index: number;
  variant?: CartItemVariant;
}

export function BundleProductCartItem({
  product,
  disableUpdate,
  index,
  variant,
}: Readonly<BundleProductCartItemProps>) {
  const t = useTranslations('CartProductInfo');
  const { toggleCartDisplay } = useCartStore();
  const { localizedLink } = useLocaleLink();

  const isLargeText = variant?.textSize === 'lg';
  // In read-only contexts (checkout, order pages) there is no cart drawer to close
  const onNavigate = disableUpdate ? undefined : toggleCartDisplay;

  return (
    <div className="flex gap-2 py-2 pl-2">
      <div
        className={`border-b-2 border-l-2 border-primary-100 ${index > 0 ? '-mt-8 h-16 w-5' : '-mt-2 h-10 w-5'} `}
      />
      <CartProductImage
        bundle
        image={product.image}
        productName={product.name}
        productPath={product.url}
        toggleCartDisplay={onNavigate}
        variant={variant}
      />

      <div className="ml-2">
        <p
          className={twJoin(
            'line-clamp-2 overflow-hidden text-ellipsis font-medium',
            isLargeText && 'sm:text-lg md:text-2xl',
          )}
        >
          {product.url ? (
            <Link href={localizedLink(product.url)} onClick={onNavigate}>
              {product.name}
            </Link>
          ) : (
            product.name
          )}
        </p>
        <p>{product.quantity > 1 && <>{product.quantity}x</>}</p>

        {product.nukiId && (
          <div
            className={twJoin('font-light text-neutral-50', isLargeText && 'sm:text-lg md:text-xl')}
          >
            {t('Connected to Nuki ID')} <span className="font-medium">{product.nukiId}</span>
          </div>
        )}

        {product.subscription && (
          <SubscriptionInfo subscription={product.subscription} isLargeText={isLargeText} />
        )}
      </div>
    </div>
  );
}
